import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import { useGetIdentity, useNotify } from 'react-admin';
import { PromoCodeDb, PromoCodeRequest, promoCodesProxy } from './apiGateway';

type PromoCodeTypeValue = 'percent' | 'fixSum';

type CreatePromoFormState = {
    type: PromoCodeTypeValue;
    code: string;
    description: string;
    amountDiscount: string;
    maxUsageCount: string;
    minSum: string;
    maxDiscountAmount: string;
    startDate: string;
    endDate: string;
};

type DeletedStateDialog = {
    code: string;
    isDeleted: boolean;
} | null;

const DATE_YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const DEFAULT_CREATE_FORM: CreatePromoFormState = {
    type: 'percent',
    code: '',
    description: '',
    amountDiscount: '',
    maxUsageCount: '1',
    minSum: '0',
    maxDiscountAmount: '',
    startDate: '',
    endDate: ''
};

function normalizePromoTypeLabel(value: string) {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'percent' || normalized === 'процент') {
        return 'Процент';
    }
    if (normalized === 'fixsum' || normalized === 'фиксированная сумма') {
        return 'Фиксированная сумма';
    }
    return value;
}

function parsePositiveOrZeroInteger(rawValue: string) {
    if (!/^\d+$/.test(rawValue)) {
        return null;
    }
    const parsed = Number(rawValue);
    if (!Number.isInteger(parsed) || parsed < 0) {
        return null;
    }
    return parsed;
}

function parsePositiveInteger(rawValue: string) {
    const parsed = parsePositiveOrZeroInteger(rawValue);
    if (parsed === null || parsed < 1) {
        return null;
    }
    return parsed;
}

function normalizePromoCodeInput(value: string) {
    return value.toUpperCase();
}

function isValidYyyyMmDd(value: string) {
    if (!DATE_YYYY_MM_DD_REGEX.test(value)) {
        return false;
    }

    const [yearRaw, monthRaw, dayRaw] = value.split('-');
    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);
    if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
        return false;
    }
    if (year < 1900 || year > 9999) {
        return false;
    }

    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function convertYyyyMmDdToDdMmYyyy(value: string) {
    if (!isValidYyyyMmDd(value)) {
        return '';
    }
    const [yearRaw, monthRaw, dayRaw] = value.split('-');
    return `${dayRaw}.${monthRaw}.${yearRaw}`;
}

function normalizePromoCodeItem(rawItem: unknown, fallbackIndex: number): PromoCodeDb | null {
    if (!rawItem || typeof rawItem !== 'object') {
        return null;
    }

    const item = rawItem as {
        code?: unknown;
        description?: unknown;
        type?: unknown;
        amount?: unknown;
        discountAmount?: unknown;
        maxUsageCount?: unknown;
        conditions?: unknown;
        condition?: unknown;
        isActive?: unknown;
        isDeleted?: unknown;
        id?: unknown;
    };

    const code = typeof item.code === 'string' ? item.code.trim() : '';
    if (!code) {
        return null;
    }

    const description = typeof item.description === 'string' ? item.description : '';
    const type = typeof item.type === 'string' ? item.type : '';
    const amountRaw = Number(item.amount ?? item.discountAmount);
    const amount = Number.isFinite(amountRaw) ? Math.trunc(amountRaw) : 0;
    const maxUsageCountRaw = Number(item.maxUsageCount);
    const maxUsageCount = item.maxUsageCount === null || typeof item.maxUsageCount === 'undefined'
        ? null
        : (Number.isFinite(maxUsageCountRaw) ? Math.trunc(maxUsageCountRaw) : null);

    const rawConditions = item.conditions;
    let conditions: string[] = [];
    if (Array.isArray(rawConditions)) {
        conditions = rawConditions
            .filter((condition): condition is string => typeof condition === 'string')
            .map((condition) => condition.trim())
            .filter(Boolean);
    } else {
        const rawCondition = item.condition;
        const conditionObject = rawCondition && typeof rawCondition === 'object'
            ? (rawCondition as { minSum?: unknown; maxDiscountAmount?: unknown; startDate?: unknown; endDate?: unknown })
            : {};
        const fallbackConditions: string[] = [];
        if (typeof conditionObject.minSum !== 'undefined' && conditionObject.minSum !== null) {
            fallbackConditions.push(`Минимальная сумма: ${String(conditionObject.minSum)}`);
        }
        if (typeof conditionObject.maxDiscountAmount !== 'undefined' && conditionObject.maxDiscountAmount !== null) {
            fallbackConditions.push(`Максимальная сумма скидки: ${String(conditionObject.maxDiscountAmount)}`);
        }
        if (typeof conditionObject.startDate === 'string' && conditionObject.startDate.trim()) {
            fallbackConditions.push(`Дата начала акции: ${conditionObject.startDate.trim()}`);
        }
        if (typeof conditionObject.endDate === 'string' && conditionObject.endDate.trim()) {
            fallbackConditions.push(`Дата конца акции: ${conditionObject.endDate.trim()}`);
        }
        conditions = fallbackConditions;
    }

    return {
        id: typeof item.id === 'string' && item.id.trim() ? item.id.trim() : `${code}-${fallbackIndex}`,
        code,
        description,
        type,
        amount,
        maxUsageCount,
        conditions,
        isActive: Boolean(item.isActive),
        isDeleted: Boolean(item.isDeleted)
    };
}

function extractPromoCodes(payload: unknown): PromoCodeDb[] {
    if (Array.isArray(payload)) {
        return payload
            .map((item, index) => normalizePromoCodeItem(item, index))
            .filter((item): item is PromoCodeDb => Boolean(item));
    }

    if (payload && typeof payload === 'object') {
        const normalizedPayload = payload as { body?: unknown; data?: unknown; items?: unknown };
        const listCandidate = normalizedPayload.body ?? normalizedPayload.data ?? normalizedPayload.items;
        if (Array.isArray(listCandidate)) {
            return listCandidate
                .map((item, index) => normalizePromoCodeItem(item, index))
                .filter((item): item is PromoCodeDb => Boolean(item));
        }
    }

    return [];
}

function formatDiscountValue(value: number, type: string) {
    const normalizedType = type.trim().toLowerCase();
    if (normalizedType === 'percent' || normalizedType === 'процент') {
        return `${value}%`;
    }
    if (normalizedType === 'fixsum' || normalizedType === 'фиксированная сумма') {
        return `${value} ₽`;
    }
    return String(value);
}

function formatMaxUsageCount(value: number | null) {
    return value === null ? 'Без лимита' : String(value);
}

function formatPromoStatus(promoCode: PromoCodeDb) {
    if (promoCode.isDeleted) {
        return 'Удален';
    }
    return promoCode.isActive ? 'Активен' : 'Истек срок';
}

function resolvePromoCreateErrorMessage(error: unknown) {
    if (typeof error === 'object' && error && 'status' in error) {
        const status = (error as { status?: unknown }).status;
        if (status === 409) {
            return 'Промокод с таким кодом уже существует';
        }
    }

    if (error instanceof Error && error.message.includes('409')) {
        return 'Промокод с таким кодом уже существует';
    }

    return 'Не удалось создать промокод';
}

export const PromoCodesPage = () => {
    const notify = useNotify();
    const { identity, isPending } = useGetIdentity();
    const currentUser = (identity ?? null) as { role?: string } | null;
    const role = currentUser?.role || '';
    const isManagerAdmin = role === 'manager-admin';
    const canAccessPromoCodes = role === 'manager-admin' || role === 'moderator-admin';

    const [promoCodes, setPromoCodes] = useState<PromoCodeDb[]>([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [deletedStateDialog, setDeletedStateDialog] = useState<DeletedStateDialog>(null);
    const [createForm, setCreateForm] = useState<CreatePromoFormState>(DEFAULT_CREATE_FORM);
    const discountFieldLabel = createForm.type === 'percent'
        ? 'Размер скидки, %'
        : 'Размер скидки, ₽';
    const discountFieldHint = createForm.type === 'percent'
        ? 'Укажите скидку в процентах'
        : 'Укажите размер скидки в рублях';
    const optionalFieldSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 152, 0, 0.08)'
        }
    };

    const loadPromoCodes = useCallback(async (targetPage: number, targetSize: number) => {
        setLoading(true);
        try {
            const payload = await promoCodesProxy.getList({ page: targetPage, size: targetSize });
            const items = extractPromoCodes(payload);
            setPromoCodes(items);
            setHasNextPage(items.length === targetSize);
        } catch (error) {
            console.error('Promo codes fetch error:', error);
            notify('Не удалось загрузить промокоды', { type: 'error' });
            setPromoCodes([]);
            setHasNextPage(false);
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        if (!canAccessPromoCodes) {
            return;
        }
        loadPromoCodes(page, size);
    }, [canAccessPromoCodes, loadPromoCodes, page, size]);

    const handleOpenCreate = () => {
        setCreateForm(DEFAULT_CREATE_FORM);
        setCreateDialogOpen(true);
    };

    const handleCloseCreate = () => {
        if (saving) {
            return;
        }
        setCreateDialogOpen(false);
        setCreateForm(DEFAULT_CREATE_FORM);
    };

    const validateCreateForm = () => {
        const code = normalizePromoCodeInput(createForm.code).trim();
        const description = createForm.description.trim();
        const amountDiscount = createForm.amountDiscount.trim();
        const maxUsageCount = createForm.maxUsageCount.trim();
        const minSum = createForm.minSum.trim();
        const maxDiscountAmount = createForm.maxDiscountAmount.trim();
        const startDate = createForm.startDate.trim();
        const endDate = createForm.endDate.trim();

        if (!code) {
            return 'Введите код промокода';
        }
        if (!description) {
            return 'Введите описание промокода';
        }
        if (!amountDiscount) {
            return 'Введите размер скидки';
        }
        if (!minSum) {
            return 'Введите минимальную сумму';
        }
        if (maxUsageCount && parsePositiveInteger(maxUsageCount) === null) {
            return 'Максимальное количество использований должно быть целым числом >= 1';
        }
        if (parsePositiveOrZeroInteger(minSum) === null) {
            return 'Минимальная сумма должна быть целым числом >= 0';
        }
        if (maxDiscountAmount && parsePositiveOrZeroInteger(maxDiscountAmount) === null) {
            return 'Ограничение по максимальной сумме скидки должно быть целым числом >= 0';
        }
        if ((startDate && !isValidYyyyMmDd(startDate)) || (endDate && !isValidYyyyMmDd(endDate))) {
            return 'Выберите корректные даты';
        }
        if (parsePositiveOrZeroInteger(amountDiscount) === null) {
            return 'Размер скидки должен быть целым числом >= 0';
        }

        return null;
    };

    const handleCreate = async () => {
        const validationError = validateCreateForm();
        if (validationError) {
            notify(validationError, { type: 'warning' });
            return;
        }

        const amountDiscount = parsePositiveOrZeroInteger(createForm.amountDiscount.trim());
        const maxUsageCountRaw = createForm.maxUsageCount.trim();
        const maxUsageCount = maxUsageCountRaw ? parsePositiveInteger(maxUsageCountRaw) : null;
        const minSumRaw = createForm.minSum.trim();
        const minSum = parsePositiveOrZeroInteger(minSumRaw);
        const maxDiscountAmountRaw = createForm.maxDiscountAmount.trim();
        const maxDiscountAmount = maxDiscountAmountRaw ? parsePositiveOrZeroInteger(maxDiscountAmountRaw) : null;
        if (
            amountDiscount === null ||
            (maxUsageCountRaw && maxUsageCount === null) ||
            minSum === null ||
            (maxDiscountAmountRaw && maxDiscountAmount === null)
        ) {
            notify('Некорректные числовые поля', { type: 'warning' });
            return;
        }

        const startDateRaw = createForm.startDate.trim();
        const endDateRaw = createForm.endDate.trim();
        const convertedStartDate = startDateRaw ? convertYyyyMmDdToDdMmYyyy(startDateRaw) : null;
        const convertedEndDate = endDateRaw ? convertYyyyMmDdToDdMmYyyy(endDateRaw) : null;
        if ((startDateRaw && !convertedStartDate) || (endDateRaw && !convertedEndDate)) {
            notify('Выберите корректные даты', { type: 'warning' });
            return;
        }

        const payload: PromoCodeRequest = {
            type: createForm.type,
            code: normalizePromoCodeInput(createForm.code).trim(),
            description: createForm.description.trim(),
            amountDiscount,
            maxUsageCount,
            condition: {
                minSum,
                maxDiscountAmount,
                startDate: convertedStartDate,
                endDate: convertedEndDate
            }
        };

        setSaving(true);
        try {
            await promoCodesProxy.create(payload);
            notify('Промокод создан', { type: 'info' });
            setCreateDialogOpen(false);
            setCreateForm(DEFAULT_CREATE_FORM);
            await loadPromoCodes(page, size);
        } catch (error) {
            console.error('Promo code create error:', error);
            notify(resolvePromoCreateErrorMessage(error), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateDeletedState = async () => {
        if (!deletedStateDialog) {
            return;
        }

        const updateRequest = deletedStateDialog;
        setSaving(true);
        setDeletedStateDialog(null);
        try {
            await promoCodesProxy.updateDeletedState(updateRequest);
            notify(updateRequest.isDeleted ? 'Промокод удален' : 'Промокод восстановлен', { type: 'info' });
            await loadPromoCodes(page, size);
        } catch (error) {
            console.error('Promo code deleted state update error:', error);
            notify(
                updateRequest.isDeleted ? 'Не удалось удалить промокод' : 'Не удалось восстановить промокод',
                { type: 'error' }
            );
        } finally {
            setSaving(false);
        }
    };

    const rowsCountLabel = useMemo(() => `Показано: ${promoCodes.length}`, [promoCodes.length]);

    if (isPending) {
        return (
            <Box sx={{ p: 3 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (!canAccessPromoCodes) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Доступ только для ролей manager-admin и moderator-admin</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4">Промокоды</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {rowsCountLabel}
                    </Typography>
                </Box>
                {isManagerAdmin ? (
                    <IconButton color="primary" onClick={handleOpenCreate} aria-label="Добавить промокод">
                        <AddCircleOutlineIcon />
                    </IconButton>
                ) : null}
            </Stack>

            {loading ? (
                <CircularProgress size={28} />
            ) : promoCodes.length === 0 ? (
                <Alert severity="info">Промокоды не найдены.</Alert>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Код</TableCell>
                                <TableCell>Описание</TableCell>
                                <TableCell>Тип</TableCell>
                                <TableCell>Скидка</TableCell>
                                <TableCell>Лимит использований</TableCell>
                                <TableCell>Условия</TableCell>
                                <TableCell>Статус</TableCell>
                                {isManagerAdmin ? <TableCell align="right">Действия</TableCell> : null}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {promoCodes.map((promoCode) => (
                                <TableRow key={promoCode.id}>
                                    <TableCell>{promoCode.code}</TableCell>
                                    <TableCell>{promoCode.description || '-'}</TableCell>
                                    <TableCell>{normalizePromoTypeLabel(String(promoCode.type || ''))}</TableCell>
                                    <TableCell>{formatDiscountValue(promoCode.amount, String(promoCode.type || ''))}</TableCell>
                                    <TableCell>{formatMaxUsageCount(promoCode.maxUsageCount)}</TableCell>
                                    <TableCell>
                                        {promoCode.conditions.length ? (
                                            <Stack spacing={0.5}>
                                                {promoCode.conditions.map((condition, index) => (
                                                    <Typography key={`${promoCode.id}-condition-${index}`} variant="body2">
                                                        {condition}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={formatPromoStatus(promoCode)}
                                            color={promoCode.isActive && !promoCode.isDeleted ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </TableCell>
                                    {isManagerAdmin ? (
                                        <TableCell align="right">
                                            {promoCode.isDeleted ? (
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => setDeletedStateDialog({
                                                        code: promoCode.code,
                                                        isDeleted: false
                                                    })}
                                                    aria-label={`Восстановить промокод ${promoCode.code}`}
                                                >
                                                    <RestoreFromTrashIcon fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setDeletedStateDialog({
                                                        code: promoCode.code,
                                                        isDeleted: true
                                                    })}
                                                    aria-label={`Удалить промокод ${promoCode.code}`}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    ) : null}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mt: 2 }}
            >
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="promo-page-size-label">Размер</InputLabel>
                    <Select
                        labelId="promo-page-size-label"
                        value={String(size)}
                        label="Размер"
                        onChange={(event) => {
                            const parsedSize = Number(event.target.value);
                            if (!Number.isFinite(parsedSize) || parsedSize <= 0) {
                                return;
                            }
                            setSize(Math.trunc(parsedSize));
                            setPage(0);
                        }}
                    >
                        <MenuItem value="10">10</MenuItem>
                        <MenuItem value="20">20</MenuItem>
                        <MenuItem value="50">50</MenuItem>
                        <MenuItem value="100">100</MenuItem>
                    </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary">
                    Страница {page}
                </Typography>

                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                        disabled={loading || page <= 0}
                    >
                        Назад
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setPage((prev) => prev + 1)}
                        disabled={loading || !hasNextPage}
                    >
                        Далее
                    </Button>
                </Stack>
            </Stack>

            <Dialog open={createDialogOpen} onClose={handleCloseCreate} maxWidth="sm" fullWidth>
                <DialogTitle>Новый промокод</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id="promo-code-type-label">Тип</InputLabel>
                            <Select
                                labelId="promo-code-type-label"
                                value={createForm.type}
                                label="Тип"
                                onChange={(event) => {
                                    setCreateForm((prev) => ({
                                        ...prev,
                                        type: event.target.value as PromoCodeTypeValue
                                    }));
                                }}
                            >
                                <MenuItem value="percent">Процент</MenuItem>
                                <MenuItem value="fixSum">Фиксированная сумма</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Код"
                            value={createForm.code}
                            onChange={(event) => setCreateForm((prev) => ({
                                ...prev,
                                code: normalizePromoCodeInput(event.target.value)
                            }))}
                            fullWidth
                        />
                        <TextField
                            label="Описание"
                            value={createForm.description}
                            onChange={(event) => setCreateForm((prev) => ({ ...prev, description: event.target.value }))}
                            fullWidth
                            multiline
                            minRows={2}
                            helperText="Обязательное поле"
                        />
                        <TextField
                            label={discountFieldLabel}
                            value={createForm.amountDiscount}
                            onChange={(event) => setCreateForm((prev) => ({ ...prev, amountDiscount: event.target.value }))}
                            helperText={discountFieldHint}
                            fullWidth
                        />
                        <TextField
                            label="Максимум использований на пользователя"
                            value={createForm.maxUsageCount}
                            onChange={(event) => setCreateForm((prev) => ({ ...prev, maxUsageCount: event.target.value }))}
                            helperText="Если пусто, использование не ограничено. По умолчанию 1"
                            fullWidth
                        />
                        <TextField
                            label="Минимальная сумма"
                            value={createForm.minSum}
                            onChange={(event) => setCreateForm((prev) => ({ ...prev, minSum: event.target.value }))}
                            helperText="Обязательное поле. Укажите 0, если ограничение по сумме не нужно"
                            fullWidth
                        />
                        <TextField
                            label="Ограничение по максимальной сумме скидки"
                            value={createForm.maxDiscountAmount}
                            onChange={(event) => setCreateForm((prev) => ({
                                ...prev,
                                maxDiscountAmount: event.target.value
                            }))}
                            helperText="Необязательное поле. Если пусто, ограничение не применяется"
                            sx={optionalFieldSx}
                            fullWidth
                        />
                        <TextField
                            label="Дата начала"
                            type="date"
                            value={createForm.startDate}
                            onChange={(event) => setCreateForm((prev) => ({ ...prev, startDate: event.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            helperText="Необязательное поле. Если указано, промокод начнет работать с этой даты"
                            sx={optionalFieldSx}
                            fullWidth
                        />
                        <TextField
                            label="Дата окончания"
                            type="date"
                            value={createForm.endDate}
                            onChange={(event) => setCreateForm((prev) => ({ ...prev, endDate: event.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            helperText="Необязательное поле. Если указано, промокод будет работать до этой даты. Без дат промокод бессрочный"
                            sx={optionalFieldSx}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCreate} disabled={saving}>Отмена</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={saving}>Создать</Button>
                </DialogActions>
            </Dialog>

            {deletedStateDialog ? (
                <Dialog open onClose={() => (saving ? null : setDeletedStateDialog(null))}>
                    <DialogTitle>{deletedStateDialog.isDeleted ? 'Удаление промокода' : 'Восстановление промокода'}</DialogTitle>
                    <DialogContent>
                        <Typography>
                            {deletedStateDialog.isDeleted ? 'Удалить' : 'Восстановить'} промокод{' '}
                            "{deletedStateDialog.code}"?
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeletedStateDialog(null)} disabled={saving}>Отмена</Button>
                        <Button
                            onClick={handleUpdateDeletedState}
                            color={deletedStateDialog.isDeleted ? 'error' : 'primary'}
                            variant="contained"
                            disabled={saving}
                        >
                            {deletedStateDialog.isDeleted ? 'Удалить' : 'Восстановить'}
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : null}
        </Box>
    );
};
